import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { ensureAuthenticated } from "../utils/auth.ts";
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
} from "@mui/material";
import { ExpandMore, CheckCircle, Error as ErrorIcon } from "@mui/icons-material";
import { useOktaAuth } from "@okta/okta-react";

export const Route = createFileRoute("/claims")({
  beforeLoad: ensureAuthenticated,
  component: ClaimsComponent,
});

interface TokenInfo {
  claims: Record<string, any>;
  scopes: string[];
  expiresAt?: number;
  issuedAt?: number;
  raw?: string;
}

function ClaimsComponent() {
  const { oktaAuth } = useOktaAuth();
  const [idToken, setIdToken] = useState<TokenInfo | null>(null);
  const [accessToken, setAccessToken] = useState<TokenInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadTokens = async () => {
      try {
        const tokens = await oktaAuth.tokenManager.getTokens();

        if (tokens.idToken) {
          setIdToken({
            claims: tokens.idToken.claims,
            scopes: [],
            expiresAt: tokens.idToken.expiresAt,
            issuedAt: tokens.idToken.claims.iat,
            raw: tokens.idToken.idToken,
          });
        }

        if (tokens.accessToken) {
          setAccessToken({
            claims: tokens.accessToken.claims,
            scopes: tokens.accessToken.scopes || [],
            expiresAt: tokens.accessToken.expiresAt,
            issuedAt: tokens.accessToken.claims.iat,
            raw: tokens.accessToken.accessToken,
          });
        }
      } catch (error: any) {
        const message = error instanceof Error ? error.message : "Failed to load tokens";
        setError(message);
      }
    };

    loadTokens();
  }, [oktaAuth]);

  const formatTimestamp = (timestamp?: number) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp * 1000).toLocaleString();
  };

  const getClaimType = (key: string): "standard" | "custom" | "authorization" => {
    // Standard JWT claims (registered claims)
    const standardClaims = ["iss", "sub", "aud", "exp", "iat", "nbf", "jti", "auth_time", "nonce", "at_hash", "c_hash"];
    // Standard OIDC claims
    const oidcClaims = ["name", "email", "email_verified", "preferred_username", "given_name", "family_name", "locale", "zoneinfo", "updated_at"];
    // Okta-specific standard claims
    const oktaClaims = ["ver", "amr", "idp", "sid"];

    if (standardClaims.includes(key) || oidcClaims.includes(key) || oktaClaims.includes(key)) {
      return "standard";
    }
    if (key.startsWith("aap-productbeheer-") || key === "groups" || key === "scp") {
      return "authorization";
    }
    return "custom";
  };

  const renderClaimsTable = (claims: Record<string, any>) => {
    const claimEntries = Object.entries(claims).map(([key, value]) => ({
      key,
      value,
      type: getClaimType(key)
    }));

    // Separate claims by type
    const standardClaims = claimEntries.filter(c => c.type === "standard");
    const authClaims = claimEntries.filter(c => c.type === "authorization");
    const customClaims = claimEntries.filter(c => c.type === "custom");

    const renderClaimRows = (claimsList: typeof claimEntries, highlightColor?: string) => (
      <>
        {claimsList.map(({ key, value }) => (
          <TableRow key={key} sx={highlightColor ? { backgroundColor: highlightColor } : {}}>
            <TableCell sx={{ fontFamily: "monospace", width: "40%" }}>
              {key}
            </TableCell>
            <TableCell>
              {Array.isArray(value) ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {value.map((v, idx) => (
                    <Chip key={idx} label={String(v)} size="small" />
                  ))}
                </Box>
              ) : typeof value === "object" ? (
                <pre style={{ margin: 0, fontSize: "0.875rem" }}>
                  {JSON.stringify(value, null, 2)}
                </pre>
              ) : (
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                  {String(value)}
                </Typography>
              )}
            </TableCell>
          </TableRow>
        ))}
      </>
    );

    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Claim Type</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {authClaims.length > 0 && (
              <>
                <TableRow>
                  <TableCell colSpan={2} sx={{ backgroundColor: "#e3f2fd", fontWeight: "bold", fontSize: "0.875rem" }}>
                    Authorization Claims ({authClaims.length})
                  </TableCell>
                </TableRow>
                {renderClaimRows(authClaims, "#f3f9ff")}
              </>
            )}
            {standardClaims.length > 0 && (
              <>
                <TableRow>
                  <TableCell colSpan={2} sx={{ backgroundColor: "#f5f5f5", fontWeight: "bold", fontSize: "0.875rem" }}>
                    Standard JWT/OIDC Claims ({standardClaims.length})
                  </TableCell>
                </TableRow>
                {renderClaimRows(standardClaims)}
              </>
            )}
            {customClaims.length > 0 && (
              <>
                <TableRow>
                  <TableCell colSpan={2} sx={{ backgroundColor: "#fff3e0", fontWeight: "bold", fontSize: "0.875rem" }}>
                    Custom Claims ({customClaims.length})
                  </TableCell>
                </TableRow>
                {renderClaimRows(customClaims, "#fffaf0")}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderScopesSection = (scopes: string[]) => {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
          Scopes ({scopes.length})
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {scopes.map((scope) => (
            <Chip
              key={scope}
              label={scope}
              color={scope.startsWith("aap-productbeheer") ? "primary" : "default"}
              icon={scope.startsWith("aap-productbeheer") ? <CheckCircle /> : undefined}
            />
          ))}
        </Box>
      </Box>
    );
  };

  const renderTokenMetadata = (token: TokenInfo) => {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
          Token Metadata
        </Typography>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell sx={{ width: "30%" }}>Issued At</TableCell>
              <TableCell>{formatTimestamp(token.issuedAt)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Expires At</TableCell>
              <TableCell>{formatTimestamp(token.expiresAt)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Valid</TableCell>
              <TableCell>
                {token.expiresAt && token.expiresAt > Date.now() / 1000 ? (
                  <Chip label="Valid" color="success" size="small" icon={<CheckCircle />} />
                ) : (
                  <Chip label="Expired" color="error" size="small" icon={<ErrorIcon />} />
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 4 }}>
        Token Claims & Information
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* ID Token Section */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6">ID Token</Typography>
            {idToken && (
              <Chip label={`${Object.keys(idToken.claims).length} claims`} size="small" />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {idToken ? (
            <>
              {renderTokenMetadata(idToken)}
              {renderClaimsTable(idToken.claims)}

              <Accordion sx={{ mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle2">Raw Token (JWT)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper
                    sx={{
                      p: 2,
                      backgroundColor: "#f5f5f5",
                      overflow: "auto",
                      maxHeight: 200,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace", wordBreak: "break-all", fontSize: "0.75rem" }}
                    >
                      {idToken.raw}
                    </Typography>
                  </Paper>
                </AccordionDetails>
              </Accordion>
            </>
          ) : (
            <Typography>No ID token available</Typography>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Access Token Section */}
      <Accordion defaultExpanded sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6">Access Token</Typography>
            {accessToken && (
              <>
                <Chip label={`${Object.keys(accessToken.claims).length} claims`} size="small" />
                <Chip label={`${accessToken.scopes.length} scopes`} size="small" color="primary" />
              </>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {accessToken ? (
            <>
              {renderTokenMetadata(accessToken)}
              {renderScopesSection(accessToken.scopes)}
              {renderClaimsTable(accessToken.claims)}

              <Accordion sx={{ mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle2">Raw Token (JWT)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper
                    sx={{
                      p: 2,
                      backgroundColor: "#f5f5f5",
                      overflow: "auto",
                      maxHeight: 200,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace", wordBreak: "break-all", fontSize: "0.75rem" }}
                    >
                      {accessToken.raw}
                    </Typography>
                  </Paper>
                </AccordionDetails>
              </Accordion>
            </>
          ) : (
            <Typography>No access token available</Typography>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Info Box */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>ID Token:</strong> Contains user identity information (name, email, etc.)
          <br />
          <strong>Access Token:</strong> Used for API authorization, contains scopes and custom claims
          <br />
          <strong>Custom Claims:</strong> Look for claims starting with "aap-productbeheer-" for authorization roles
        </Typography>
      </Alert>
    </Container>
  );
}
